import { useMemo } from 'react';

import { routePaths as adminRoutePaths } from '@apps/admin/route.paths';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useRouterHelper, useTranslation } from '@shared/core';
import { Bed, Chair, ContactBook, EditPages, GuestList, RsvpAdmin, Venue, Surprise } from '@withjoy/joykit/icons';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import type { PageInfo } from './useProductRows';
import type { ProductRowItemProps } from '../../PrimaryDashboardContent/ProductRowItem';
import { usePlanningRoutes } from '@apps/admin/routes/Planning/hooks/usePlanningRoutes';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestListRoute } from '@shared/utils/eventInfo/useGuestListRoute';
import { useRsvpRoute } from '@shared/utils/eventInfo/useRsvpRoute';
import { useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import { useHasCashFunds } from '@apps/admin/routes/CashFunds/shared/hooks/useHasCashFunds';
import { useExperienceVersion } from '@shared/components/ExperienceSwitch';
import { useHasSeatingPlus } from '@shared/hooks/useHasSeatingPlus/useHasSeatingPlus';

const useWeddingShopFeature = (): ProductRowItemProps | null => {
  const { value, payload } = useFeatureValue('weddingShopFeatureEnabled');
  const { t2 } = useTranslation('adminDashboardV2');

  const isFeatureEnabled = value !== 'off' && value !== undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maybeTileTitle = (payload as any)?.tileTitle;

  return useMemo(() => {
    if (isFeatureEnabled) {
      return {
        id: 'rsvp-weddingShop',
        title: maybeTileTitle || t2('sections').guestList.children.weddingShop.title,
        icon: Venue,
        path: 'https://weddingshop.withjoy.com',
        pathIsInJoyWeb: false,
        hasNewPill: false,
        telemetryName: 'weddingShop',
      };
    }

    return null;
  }, [isFeatureEnabled, t2, maybeTileTitle]);
};

export const useGuestListRsvpProductRow = (_eventPages: ReadonlyArray<PageInfo>, productVerticals: ProductVerticals): ProductRow => {
  const routeHelpers = useRouterHelper();
  const hotelRoute = useHotelRoutePaths();
  const telemetry = useDashboardTelemetry();
  const { t2, tq } = useTranslation('adminDashboardV2');
  const { guestList: guestListTranslations } = t2('sections');
  const { eventHandle, eventInfo } = useEventInfo();
  const adminDashboardHoneymoonEnabled = useFeatureValue('adminDashboardHoneymoonEnabled').value === 'on';
  const { visible: showCashFunds, hasNewPill: cashFundsHasNewPill } = useHasCashFunds(eventInfo?.eventId);
  // Advertise the redesigned RSVP Responses page on the RSVP tile (dashboard + burger menu) until the admin opts into it.
  const { showNewPill: showRsvpNewPill } = useExperienceVersion('rsvpResponses');
  const isHotelBlockEnabled = useFeatureValue('hotelBlockEnabled').value === 'on';
  // Skip keeps Amplitude exposure scoped to admins who can actually see the Guest List row.
  const [isSeatingChartEnabled] = useIsFeatureEnabled('seatingChartEnabled', { skip: !!productVerticals.optOutOfGuestList });
  // The flag buckets by user, but the feature is bought per event: a collaborator on a paid
  // event must see the entry even when their own user lands in the rollout's off bucket.
  // Mirrors the Messaging+ pattern (usePaidFeature gates entry points on the event
  // entitlement, not a user flag) and the MFE's own enforcement (useSeatingMode).
  const { hasSeatingPlus } = useHasSeatingPlus(eventInfo?.eventId ?? '', { skip: !!productVerticals.optOutOfGuestList });
  const showSeatingChartEntry = isSeatingChartEnabled || hasSeatingPlus;
  const weddingShopTile = useWeddingShopFeature();
  const { hotelBlockPath } = usePlanningRoutes();
  const guestListRoute = useGuestListRoute();
  const rsvpRoute = useRsvpRoute();

  const guestListRsvpProductRow = useMemo<ProductRow>(() => {
    const guestListStrings = guestListTranslations;
    const isEnabled = !productVerticals.optOutOfGuestList;

    const children: Array<ProductRowItemProps> = [
      {
        id: 'rsvp-guestList',
        title: guestListStrings.children.guestList.title,
        icon: GuestList,
        // New-stack weddings and general events have no Firebase document, so the legacy
        // `/edit/guests` route 404s for them. useGuestListRoute picks the Firebase-free
        // route instead. See MGT-65.
        path: routeHelpers.buildPath(guestListRoute.segment),
        pathIsInJoyWeb: guestListRoute.pathIsInJoyWeb,
        hideDropdownLabel: true,
        // The Seating Chart entry hides behind the chevron; the tile pill is the only cue it's there.
        hasNewPill: showSeatingChartEntry,
        onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'guestList', section: fromDropdown ? 'Guest List' : '', categoryArg }),
        telemetryName: guestListRoute.segment,
        subItems: [
          {
            title: guestListStrings.children.guestList.title,
            icon: GuestList,
            path: routeHelpers.buildPath(guestListRoute.segment),
            pathIsInJoyWeb: guestListRoute.pathIsInJoyWeb,
            onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'guestList', section: fromDropdown ? 'Guest List' : '', categoryArg }),
            telemetryName: guestListRoute.segment,
          },
          ...(showSeatingChartEntry
            ? [
                {
                  title: tq`Seating Chart`,
                  icon: Chair,
                  path: routeHelpers.buildPath(adminRoutePaths.seatingChart.path),
                  pathIsInJoyWeb: true,
                  hasNewPill: true,
                  onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'seatingChart', section: fromDropdown ? 'Guest List' : '', categoryArg }),
                  telemetryName: adminRoutePaths.seatingChart.path,
                },
              ]
            : []),
          {
            title: tq`RSVP Tracking`,
            icon: RsvpAdmin,
            // The legacy rsvp admin reads the legacy RSVP API by firebaseId, so it errors for an
            // event with no Firebase document. useRsvpRoute sends those to the registration
            // screen's tracking tab instead. See MGT-65.
            path: routeHelpers.buildPath(rsvpRoute.tracking),
            pathIsInJoyWeb: true,
            onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'rsvpTracking', section: fromDropdown ? 'Guest List' : '', categoryArg }),
            telemetryName: rsvpRoute.tracking,
          },
        ],
      },
      {
        id: 'rsvp-contactCollector',
        title: guestListStrings.children.contactCollector.title,
        icon: ContactBook,
        path: routeHelpers.buildPath(adminRoutePaths.contact.path),
        onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'contactCollector', section: fromDropdown ? 'Contact Collector' : '', categoryArg }),
        hasNewPill: false,
        pathIsInJoyWeb: true,
        telemetryName: adminRoutePaths.contact.path,
      },
      {
        id: 'rsvp-rsvp',
        // Use the shorter "RSVP" label (matching the RSVP page header) instead of the locale's
        // "Manage RSVP" so the burger nav / command palette entry reads consistently.
        title: tq`RSVP`,
        icon: RsvpAdmin,
        // Same reason as RSVP Tracking above — the Firebase-free equivalent is the registration
        // screen, which is where the workspace nav already sends these events.
        path: routeHelpers.buildPath(rsvpRoute.manage),
        // Both destinations are joy-web SPA routes (Admin.controller.tsx registers `rsvp` and
        // `registration`), same as the RSVP Tracking sub-item above. Omitting this forced a full
        // app boot on every click.
        pathIsInJoyWeb: true,
        onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'rsvp', section: fromDropdown ? 'Guest List' : '', categoryArg }),
        hasNewPill: showRsvpNewPill,
        telemetryName: rsvpRoute.manage,
      },
    ];

    const moreChildren: Array<ProductRowItemProps> = [];

    if (weddingShopTile) {
      if (showCashFunds) {
        // When in dropdown, omit icon for a cleaner list appearance
        const { icon: _omitIcon, ...tileWithoutIcon } = weddingShopTile;
        moreChildren.push(tileWithoutIcon);
      } else {
        children.push(weddingShopTile);
      }
    }

    if (!adminDashboardHoneymoonEnabled && isHotelBlockEnabled) {
      const roomBlocksTile = {
        id: 'rsvp-hotelRoomBlock',
        title: guestListStrings.children.hotels.children.roomBlocks.title,
        hideDropdownLabel: true,
        icon: Bed,
        path: hotelBlockPath,
        onClick: (categoryArg?: string, fromDropdown?: boolean) =>
          telemetry.openPage({ label: 'adminRoomBlockTile', section: fromDropdown ? 'Hotel Room Block' : '', categoryArg }),
        telemetryName: PlanningRoutes.hotelblock,
        pathIsInJoyWeb: true,
      } as ProductRowItemProps;
      if (showCashFunds) {
        moreChildren.push(roomBlocksTile);
      } else {
        children.push(roomBlocksTile);
      }
    }

    if (adminDashboardHoneymoonEnabled) {
      const hotelSubItems = [];

      if (isHotelBlockEnabled) {
        hotelSubItems.push({
          id: 'rsvp-hotelRoomBlock-honeymoon',
          title: guestListStrings.children.hotels.children.roomBlocks.title,
          path: hotelBlockPath,
          onClick: (categoryArg?: string, fromDropdown?: boolean) =>
            telemetry.openPage({ label: 'adminRoomBlockTile', section: fromDropdown ? 'Hotel Room Block' : '', categoryArg }),
          telemetryName: PlanningRoutes.hotelblock,
        });
      }

      if (eventHandle) {
        hotelSubItems.push({
          id: 'rsvp-honeymoon',
          title: guestListStrings.children.hotels.children.honeymoon.title,
          path: hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { utm_medium: 'web', utm_source: 'dashboard', utm_campaign: 'guest_list_rsvp_menu' }),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'adminHoneymoonTile', section: fromDropdown ? 'Honeymoon' : '', categoryArg }),
          telemetryName: '/honeymoon',
        });
      }

      const hotelsParent = {
        id: 'rsvp-hotels',
        title: guestListStrings.children.hotels.title,
        hideDropdownLabel: true,
        icon: EditPages,
        tileBehavior: 'openDropdown',
        subItems: hotelSubItems,
      } as ProductRowItemProps;

      if (showCashFunds) {
        // flatten into More menu rather than nesting Hotels submenu under More
        moreChildren.push(...hotelSubItems);
      } else {
        children.push(hotelsParent);
      }
    }

    if (showCashFunds) {
      // Add Manage Collections entry
      moreChildren.unshift({
        id: 'rsvp-manageCollections',
        title: tq`Cash Funds`,
        path: routeHelpers.buildPath(adminRoutePaths.cashfunds.path),
        onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'manageCollections', section: fromDropdown ? 'Cash Funds' : '', categoryArg }),
        telemetryName: adminRoutePaths.cashfunds.path,
        pathIsInJoyWeb: true,
        hasNewPill: cashFundsHasNewPill,
      });

      children.push({
        id: 'rsvp-rsvpMore',
        title: tq`More`,
        hideDropdownLabel: true,
        icon: Surprise,
        tileBehavior: 'openDropdown',
        subItems: moreChildren,
      });
    }

    return {
      title: guestListStrings.title,
      isEnabled,
      children,
    };
  }, [
    guestListTranslations,
    productVerticals,
    routeHelpers,
    weddingShopTile,
    adminDashboardHoneymoonEnabled,
    telemetry,
    hotelBlockPath,
    hotelRoute.hotelSearch,
    eventHandle,
    isHotelBlockEnabled,
    showSeatingChartEntry,
    showCashFunds,
    cashFundsHasNewPill,
    showRsvpNewPill,
    tq,
    guestListRoute,
    rsvpRoute,
  ]);

  return guestListRsvpProductRow;
};
